// Sprint agent-stages — Stages store.
//
// Owns the canonical list of workflow stages ordered by sortOrder.
// Loaded once at dashboard mount (DashboardLayout) and mutated
// optimistically; every mutation adopts the backend's canonical row on
// success and rolls back with a toast on failure (same contract as
// projects.store.ts, minus the multi-server broadcast — stages are
// control-plane-only state post-D-013).
//
// `toggleCollapsed` is deliberately fire-and-forget (spec pre-mortem #5):
// the section header must respond instantly and a lost write costs one
// tap, so there is no retry loop.

import { create } from 'zustand';
import { api } from '../lib/api';
import { useToastStore } from './toast.store';
import { t } from '../i18n';
import type { Stage } from '../types';
import { useAppStore } from './app.store';

export interface StagesState {
  stages: Stage[];
  loaded: boolean;

  load: () => Promise<void>;
  createStage: (name: string, color: string) => Promise<Stage | null>;
  updateStage: (
    id: string,
    patch: { name?: string; color?: string },
  ) => Promise<void>;
  toggleCollapsed: (id: string) => void;
  reorder: (orderedIds: string[]) => Promise<void>;
  removeStage: (id: string) => Promise<boolean>;
}

function sortByOrder(list: Stage[]): Stage[] {
  return [...list].sort((a, b) => {
    if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
    return a.id.localeCompare(b.id);
  });
}

export const useStagesStore = create<StagesState>((set, get) => ({
  stages: [],
  loaded: false,

  async load() {
    try {
      const res = await api<{ stages: Stage[] }>('/api/stages');
      set({ stages: sortByOrder(res.stages ?? []), loaded: true });
    } catch {
      // Sidebar falls back to "Sin estado" + "Archivado" only; a reload
      // (or the next load() call) recovers. Marked loaded so the UI
      // doesn't wait forever on a dead backend.
      set({ loaded: true });
    }
  },

  async createStage(name, color) {
    try {
      const res = await api<{ stage: Stage }>('/api/stages', {
        method: 'POST',
        body: { name, color },
      });
      set((s) => ({ stages: sortByOrder([...s.stages, res.stage]) }));
      return res.stage;
    } catch {
      useToastStore.getState().push({
        message: t('stages.createError'),
        durationMs: 4000,
      });
      return null;
    }
  },

  async updateStage(id, patch) {
    const before = get().stages;
    const idx = before.findIndex((s) => s.id === id);
    const current = before[idx];
    if (!current) return;
    const optimistic = [...before];
    optimistic[idx] = { ...current, ...patch };
    set({ stages: optimistic });
    try {
      const res = await api<{ stage: Stage }>(`/api/stages/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: patch,
      });
      set((s) => ({
        stages: sortByOrder(s.stages.map((x) => (x.id === id ? res.stage : x))),
      }));
    } catch {
      set({ stages: before });
      useToastStore.getState().push({
        message: t('stages.updateError'),
        durationMs: 4000,
      });
    }
  },

  toggleCollapsed(id) {
    const current = get().stages.find((s) => s.id === id);
    if (!current) return;
    const collapsed = !current.collapsed;
    set((s) => ({
      stages: s.stages.map((x) => (x.id === id ? { ...x, collapsed } : x)),
    }));
    void api(`/api/stages/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { collapsed },
    }).catch(() => {
      // Fire-and-forget by design — see header comment.
    });
  },

  async reorder(orderedIds) {
    const before = get().stages;
    const optimistic = orderedIds
      .map((id, idx) => {
        const s = before.find((x) => x.id === id);
        return s ? { ...s, sortOrder: (idx + 1) * 100 } : null;
      })
      .filter((s): s is Stage => s !== null);
    if (optimistic.length !== before.length) {
      await get().load();
      return;
    }
    set({ stages: optimistic });
    try {
      const res = await api<{ ok: boolean; stages: Stage[] }>('/api/stages/reorder', {
        method: 'POST',
        body: { order: orderedIds.map((id, idx) => ({ id, sortOrder: (idx + 1) * 100 })) },
      });
      set({ stages: sortByOrder(res.stages) });
    } catch {
      set({ stages: before });
      useToastStore.getState().push({
        message: t('stages.updateError'),
        durationMs: 4000,
      });
    }
  },

  async removeStage(id) {
    const before = get().stages;
    set((s) => ({ stages: s.stages.filter((x) => x.id !== id) }));
    try {
      await api<{ ok: boolean; clearedAgentIds: string[] }>(
        `/api/stages/${encodeURIComponent(id)}`,
        { method: 'DELETE' },
      );
      // Backend cleared the rows transactionally; sweep the local mirror
      // so the agents re-render under "Sin estado" without a reload.
      useAppStore.getState().clearAgentsStageLocally(id);
      return true;
    } catch {
      set({ stages: before });
      useToastStore.getState().push({
        message: t('stages.deleteError'),
        durationMs: 4000,
      });
      return false;
    }
  },
}));
